import { IconButton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { TIconName } from '@legacy/assets/variables/icon';
import { Icon } from '@legacy/components/ui-components';
import {
  AssignFlowStore,
  IAssignFlowValue,
} from '@legacy/stores/ResourcePlannerStore/AssignFlowStore';
import { AssignFlowHideTooltipKey } from '../../localStorageKeys';
import { RemoveTaskOrResourceDialog } from './components/RemoveTaskOrResourceDialog';

interface IAssignFlow {
  canAssign: boolean;
  canDelete: boolean;
  isWorkItem: boolean;
  isTask: boolean;
  value: IAssignFlowValue;
}

export const AssignFlow = ({
  canAssign,
  canDelete,
  isTask,
  isWorkItem,
  value,
}: IAssignFlow) => {
  const { t } = useTranslation('assignFlow');
  const location = useLocation();

  const assignFlowTooltipKey = localStorage.getItem(AssignFlowHideTooltipKey);
  const [hideTooltip, setHideTooltip] = useState(
    assignFlowTooltipKey === 'true',
  );
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const itemId = isWorkItem
    ? value.workItemSourceReferenceId
    : value.resourceSourceReferenceId;

  const actionIcon: {
    iconName: TIconName;
    id: string;
    label: string;
    action?: () => void;
  } = useMemo(() => {
    if (canDelete) {
      return {
        iconName: isWorkItem ? 'inventoryMinus' : 'personOutlineMinus',
        id: 'RemoveTaskOrEmployee',
        label: isWorkItem ? t('RemoveTask') : t('RemoveEmployee'),
        action: () => setDialogIsOpen(true),
      };
    }

    if (isTask) {
      return {
        iconName: 'personOutlinePlus',
        id: 'AssignTaskToEmployee',
        label: isWorkItem ? t('AssignToEmployee') : t('AssignToTask'),
      };
    }

    // isEmployee
    return {
      iconName: 'inventoryPlus',
      id: 'AssignEmployeeToTask',
      label: isWorkItem ? t('AssignToEmployee') : t('AssignToTask'),
    };
  }, [canDelete, isTask, isWorkItem, t]);

  const dialog = useMemo(() => {
    const dialogProps = { dialogIsOpen, setDialogIsOpen };

    return <RemoveTaskOrResourceDialog {...dialogProps} />;
  }, [dialogIsOpen]);

  const url = () => {
    if (isTask) {
      return `${location.pathname}/assign-task/${itemId}?projectId=${
        value.parentProject?.id
      }&name=${encodeURIComponent(value.name)}&endsAt=${encodeURIComponent(
        value.endsAt?.toISOString(),
      )}&startsAt=${encodeURIComponent(value.startsAt?.toISOString())}`;
    }
    return `${location.pathname}/assign-resource/${itemId}?name=${encodeURIComponent(value.name)}`;
  };

  useEffect(() => {
    localStorage.setItem(AssignFlowHideTooltipKey, String(hideTooltip));
  }, [dialogIsOpen, hideTooltip]);

  return (
    <AssignFlowStore value={{ ...value, hideTooltip, setHideTooltip }}>
      {canDelete && (
        <>
          <IconButton
            color="inherit"
            data-automation-id={`${actionIcon.id}IconButton-${itemId}`}
            onClick={actionIcon.action}
            size="small"
            title={actionIcon.label}
          >
            <Icon iconName={actionIcon.iconName} />
          </IconButton>

          {dialogIsOpen ? dialog : null}
        </>
      )}
      {canAssign ? (
        <IconButton
          component={Link}
          to={url()}
          color="inherit"
          data-automation-id={`${actionIcon.id}IconButton-${itemId}`}
          title={actionIcon.label}
          size="small"
        >
          <Icon iconName={actionIcon.iconName} />
        </IconButton>
      ) : null}
    </AssignFlowStore>
  );
};
